import React, { useState } from 'react';
import { useTranslation } from "react-i18next";

const GenericTable = ({
                          data,
                          columnsToIgnore = [],
                          priorityColumns = [],
                          translationKeyPrefix,
                          additionalFieldsEdit,
                          onEdit,
                          nonEditableFields = [],
                          onDelete,
                          customFieldRenderers = {},
                          customDisplayRenderers = {}
                      }) => {
    const { t } = useTranslation();
    const [editingId, setEditingId] = useState(null);
    const [editBuffer, setEditBuffer] = useState({});

    const flattenObject = (obj, parentKey = '', result = {}) => {
        Object.entries(obj).forEach(([key, value]) => {
            const fullKey = parentKey ? `${parentKey}.${key}` : key;
            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                flattenObject(value, fullKey, result);
            } else {
                result[fullKey] = value;
            }
        });
        return result;
    };

    const flattenKeys = (obj, parentKey = '', keys = []) => {
        Object.entries(obj).forEach(([key, value]) => {
            const fullKey = parentKey ? `${parentKey}.${key}` : key;
            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                flattenKeys(value, fullKey, keys);
            } else {
                keys.push(fullKey);
            }
        });
        return keys;
    };

    const handleEditClick = (row) => {
        setEditingId(row.id);
        setEditBuffer(flattenObject(row));
    };

    const handleInputChange = (key, value) => {
        setEditBuffer(prev => ({ ...prev, [key]: value }));
    };

    const buildNestedPayload = (original, updated) => {
        const result = {};
        Object.entries(updated).forEach(([key, newVal]) => {
            const origVal = original[key];
            if (newVal !== origVal) {
                const parts = key.split('.');
                let current = result;

                for (let i = 0; i < parts.length; i++) {
                    const part = parts[i];
                    if (i === parts.length - 1) {
                        current[part] = { value: newVal };
                    } else {
                        current[part] = current[part] || {};
                        current = current[part];
                    }
                }
            }
        });
        return result;
    };

    const handleSave = () => {
        const originalRow = flattenObject(data.find(row => row.id === editingId));
        const payload = buildNestedPayload(originalRow, editBuffer);

        if (Object.keys(payload).length > 0 && onEdit) {
            onEdit({ id: editingId, ...payload , ...additionalFieldsEdit});
        }
        setEditingId(null);
        setEditBuffer({});
    };

    const renderTableHeader = (keys) => (
        <>
            {keys.map((key) => (
                <th key={key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t(`${translationKeyPrefix}.${key}`)}
                </th>
            ))}
            {(onEdit || onDelete) && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.actions')}</th>}
        </>
    );

    const renderTableRow = (item, keys) => {
        const flattened = flattenObject(item);

        return (
            <tr key={item.id || Math.random()} className="hover:bg-gray-50 transition-colors">
                {keys.map((key) => (
                    <td key={key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {editingId === item.id && !nonEditableFields.includes(key) ? (
                            customFieldRenderers[key] ? (
                                customFieldRenderers[key](editBuffer[key] || '', (value) => handleInputChange(key, value))
                            ) : (
                                <input
                                    type="text"
                                    value={editBuffer[key] || ''}
                                    onChange={(e) => handleInputChange(key, e.target.value)}
                                    className="border px-2 py-1 rounded w-full"
                                />
                            )
                        ) : (
                            customDisplayRenderers[key] ? customDisplayRenderers[key](flattened[key]) : flattened[key]
                        )}
                    </td>
                ))}
                {(onEdit || onDelete) && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {editingId === item.id ? (
                            <div className="flex gap-2">
                                <button onClick={handleSave} className="text-green-600 hover:text-green-800 transition-colors" title="Save">✔️</button>
                                <button onClick={() => setEditingId(null)} className="text-red-600 hover:text-red-800 transition-colors" title="Cancel">❌</button>
                            </div>
                        ) : (
                            <div className="flex gap-2">
                                {onEdit && (
                                    <button onClick={() => handleEditClick(item)} className="text-blue-600 hover:text-blue-800 transition-colors" title="Edit">✏️</button>
                                )}
                                {onDelete && (
                                    <button onClick={() => onDelete(item)} className="text-red-600 hover:text-red-800 transition-colors" title="Delete">🗑️</button>
                                )}
                            </div>
                        )}
                    </td>
                )}
            </tr>
        );
    };

    if (!data || data.length === 0) return null;

    let allKeys = flattenKeys(data[0]);
    let filteredKeys = allKeys.filter((key) => !columnsToIgnore.includes(key));

    if (priorityColumns.length > 0) {
        const prioritySet = new Set(priorityColumns);
        const prioritized = filteredKeys.filter(k => prioritySet.has(k));
        const nonPrioritized = filteredKeys.filter(k => !prioritySet.has(k));
        filteredKeys = [
            ...priorityColumns.filter(pc => prioritized.includes(pc)),
            ...nonPrioritized
        ];
    }

    return (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                    <tr>
                        {renderTableHeader(filteredKeys)}
                    </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                    {data.map((row) => renderTableRow(row, filteredKeys))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default GenericTable;
